import { useState } from 'react';
import { Dialog, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { ApiError, resellerRefusalMessage } from '@/api/errors';
import type { ResellerDto } from '@/api/tmm/types';
import { CreateResellerForm } from '../resellers/CreateResellerDialog';
import { CreateTenantForm } from '../tenants/CreateTenantDialog';

export function NewPartnerDialog({
  open,
  onClose,
  onOutcomeUnknown,
}: {
  open: boolean;
  onClose: () => void;
  onOutcomeUnknown: () => void;
}) {
  return open ? <NewPartnerSteps onClose={onClose} onOutcomeUnknown={onOutcomeUnknown} /> : null;
}

function NewPartnerSteps({
  onClose,
  onOutcomeUnknown,
}: {
  onClose: () => void;
  onOutcomeUnknown: () => void;
}) {
  const { t } = useCopy();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [partner, setPartner] = useState<ResellerDto | null>(null);
  // `paymentsLink`: the warning is the billing one (the tenant exists), so the page offers Setup → Payments.
  const finish = (warning?: string, paymentsLink = false) => {
    if (partner) {
      onClose();
      void navigate(
        `/partners/${partner.id}`,
        warning
          ? {
              state: {
                creationWarning: warning,
                ...(paymentsLink ? { creationWarningPayments: true } : {}),
              },
            }
          : undefined,
      );
    }
  };
  return (
    <Dialog
      open
      onClose={() => {
        if (!pending) (partner ? finish : onClose)();
      }}
      fullWidth
      maxWidth="sm"
    >
      <Box sx={{ px: 3, pt: 2 }}>
        <Typography variant="subtitle1">{t('partners.new', 'New partner…')}</Typography>
        <Typography>
          {partner
            ? t('partners.step2', 'Step 2 of 2 — Create tenant')
            : t('partners.step1', 'Step 1 of 2 — Create partner')}
        </Typography>
      </Box>
      {partner ? (
        <CreateTenantForm
          onPendingChange={setPending}
          resellers={[]}
          presetReseller={partner}
          onClose={finish}
          onCreated={(_slug, billingWarning) => finish(billingWarning, true)}
          onFailed={(error) => {
            const message =
              error instanceof ApiError && String(error.code) === 'RESELLER_HAS_TENANT'
                ? t('tenants.create.hasTenant', 'This reseller already has a tenant.')
                : (resellerRefusalMessage(error, t) ??
                  // The partner exists; this one line says so and what to do, whatever the code.
                  t(
                    'partners.tenantFailed',
                    'Partner created. Could not confirm tenant creation. Check the partner page before trying again.',
                  ));
            finish(message);
          }}
        />
      ) : (
        <CreateResellerForm
          onPendingChange={setPending}
          open
          onClose={onClose}
          onCreated={setPartner}
          onOutcomeUnknown={onOutcomeUnknown}
        />
      )}
    </Dialog>
  );
}
